import { useEffect, useContext, useMemo, useCallback } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import getValidationMessage from 'helpers/getValidationMessage';

import { getMandatoryFileValidations } from 'views/shared/ReferToUnderwriting/helpers';

import {
  addMissingMandatoryField,
  removeMissingMandatoryField,
  addRemark,
  removeRemark,
  updateRemark,
  addFile,
  removeFile,
} from 'reducers/autoPersonalFormReducer';

import { getFieldId } from '../resolverUtils';
import type {
  UseGapInsuranceResolverConfig,
  GapInsuranceFieldIds,
  GapInsuranceResolverState,
  GapInsuranceResolverHandlers,
  LapseReasonOption,
} from '../types';

export const LAPSE_REASON_LABELS: Record<LapseReasonOption, string> = {
  a: 'Non Payment',
  b: 'Non-disclosure',
  c: 'Misrepresentation',
  d: 'Other',
};

const parse090aText = (text: string) => {
  const normalized = text.trim();
  if (!normalized) return { option: null, otherText: '' };
  if (normalized.includes('<Non Payment>'))
    return { option: 'a' as LapseReasonOption, otherText: '' };
  if (normalized.includes('<Non-disclosure>'))
    return { option: 'b' as LapseReasonOption, otherText: '' };
  if (normalized.includes('<Misrepresentation>'))
    return { option: 'c' as LapseReasonOption, otherText: '' };
  const otherMatch = normalized.match(/^Reason for Lapse:\s*<Other:\s*(.+)>$/i);
  if (otherMatch)
    return {
      option: 'd' as LapseReasonOption,
      otherText: otherMatch[1],
    };
  return { option: null, otherText: '' };
};

const get090aRemarkText = (
  option: LapseReasonOption | null,
  otherText: string,
) => {
  if (!option) return '';
  if (option === 'd') {
    if (!otherText.trim()) return '';
    return `Reason for Lapse: <Other: ${otherText}>`;
  }
  return `Reason for Lapse: <${LAPSE_REASON_LABELS[option]}>`;
};

export const useGapInsuranceResolver = (
  config: UseGapInsuranceResolverConfig,
) => {
  const { validationId, section, itemKey, formStateKey, fileType, fileLabel } =
    config;

  const { validations } = useContext(ApplicationFormContext);
  const dispatch = useAppDispatch();

  const mandatoryFileValidations = getMandatoryFileValidations(formStateKey);

  const alertMessage = getValidationMessage(validations, validationId);
  const show = !!alertMessage;

  const remark = useAppSelector((state) => {
    return state[formStateKey].remarks.find(
      (r: any) => r.validation_key === validationId && r.item_key === itemKey,
    );
  });

  const file = useAppSelector((state) => {
    return state[formStateKey].files.find(
      (f: any) => f.validation_key === validationId && f.item_key === itemKey,
    );
  });

  const customData =
    remark?.custom_data?.type === 'lapse' ? remark.custom_data : null;

  // Parse reason option from custom_data or text
  const reasonOption = useMemo(() => {
    if (customData?.reason_option) return customData.reason_option;
    if (remark?.text) return parse090aText(remark.text).option;
    return null;
  }, [customData, remark?.text]);

  // Parse other text from remark text
  const otherText = useMemo(() => {
    if (remark?.text) return parse090aText(remark.text).otherText;
    return '';
  }, [remark?.text]);

  const noClaimsConfirmed = customData?.no_claims_confirmed || false;

  // Generate field IDs for mandatory field tracking and navigation
  const fieldIds: GapInsuranceFieldIds = useMemo(
    () => ({
      reasonOption: getFieldId('Gap Insurance Reason', itemKey),
      otherExplanation: getFieldId('Gap Insurance Other Explanation', itemKey),
      noClaimsConfirmed: getFieldId(
        'Gap Insurance No Claims Confirmation',
        itemKey,
      ),
    }),
    [itemKey],
  );

  // Initialize remark and file when validation is shown
  useEffect(() => {
    if (show) {
      dispatch(
        addRemark({
          validation_key: validationId,
          item_key: itemKey,
          text: '',
          status: 'created',
          text_validator_log_id: null,
          ai_result: '',
          section,
          custom_data: {
            type: 'lapse',
            reason_option: null,
            no_claims_confirmed: false,
          },
        }),
      );

      dispatch(
        addFile({
          validation_key: validationId,
          item_key: itemKey,
          status: 'created',
          file_validator_log_id: null,
          ai_result: '',
          section,
          type: fileType,
          files: [],
          text: fileLabel ?? 'Photos of the vehicle - Gap in Insurance (090a)',
          mandatory: mandatoryFileValidations.includes(validationId),
        }),
      );
    } else {
      dispatch(
        removeRemark({
          validation_key: validationId,
          item_key: itemKey,
        }),
      );
      dispatch(
        removeFile({
          validation_key: validationId,
          item_key: itemKey,
        }),
      );
    }
  }, [
    show,
    validationId,
    itemKey,
    section,
    fileType,
    fileLabel,
    mandatoryFileValidations,
    dispatch,
  ]);

  // Manage mandatory field validation
  useEffect(() => {
    if (!show || !remark) {
      dispatch(removeMissingMandatoryField(fieldIds.reasonOption));
      dispatch(removeMissingMandatoryField(fieldIds.otherExplanation));
      dispatch(removeMissingMandatoryField(fieldIds.noClaimsConfirmed));
      return;
    }

    // Reason option validation
    if (!reasonOption) {
      dispatch(
        addMissingMandatoryField({
          name: fieldIds.reasonOption,
          section,
        }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.reasonOption));
    }

    // Other explanation validation (only when "d" is selected)
    if (reasonOption === 'd' && (!otherText || otherText.trim().length === 0)) {
      dispatch(
        addMissingMandatoryField({
          name: fieldIds.otherExplanation,
          section,
        }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.otherExplanation));
    }

    // No claims confirmation validation
    if (!noClaimsConfirmed) {
      dispatch(
        addMissingMandatoryField({
          name: fieldIds.noClaimsConfirmed,
          section,
        }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.noClaimsConfirmed));
    }
  }, [
    show,
    remark,
    reasonOption,
    otherText,
    noClaimsConfirmed,
    section,
    fieldIds,
    dispatch,
  ]);

  useEffect(() => {
    if (!show || !remark || !reasonOption || reasonOption === 'd') return;
    if (remark.status === 'approved') return;

    dispatch(
      updateRemark({
        validation_key: validationId,
        item_key: itemKey,
        data: {
          status: 'approved',
          ai_result: `Pass: The remark provides a whitelisted reason (${LAPSE_REASON_LABELS[reasonOption]}) which is sufficient to account for the gap in coverage.`,
        },
      }),
    );
  }, [show, remark, reasonOption, validationId, itemKey, dispatch]);

  // Handlers
  const handleReasonOptionChange = useCallback(
    (option: LapseReasonOption) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedOption = e.target.checked ? option : null;
      const currentOtherText = selectedOption === 'd' ? otherText : '';
      const text = get090aRemarkText(selectedOption, currentOtherText);

      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            text,
            status: text ? 'created' : 'created',
            custom_data: {
              type: 'lapse',
              reason_option: selectedOption,
              no_claims_confirmed: noClaimsConfirmed,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, otherText, noClaimsConfirmed],
  );

  const handleOtherTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const text = get090aRemarkText(reasonOption, e.target.value);

      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            text,
            status: 'created',
            custom_data: {
              type: 'lapse',
              reason_option: reasonOption,
              no_claims_confirmed: noClaimsConfirmed,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, reasonOption, noClaimsConfirmed],
  );

  const handleNoClaimsConfirmedChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = get090aRemarkText(reasonOption, otherText);

      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            text,
            custom_data: {
              type: 'lapse',
              reason_option: reasonOption,
              no_claims_confirmed: e.target.checked,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, reasonOption, otherText],
  );

  // Determine compact alert status based on completion state
  const getCompactAlertStatus = useCallback(() => {
    if (!remark) return 'warning';
    if (!reasonOption) return 'warning';
    if (reasonOption === 'd' && !otherText?.trim()) return 'warning';
    if (!noClaimsConfirmed) return 'warning';
    if (file && file.status !== 'approved' && !file.is_deferred)
      return 'warning';
    return 'success';
  }, [remark, file, reasonOption, otherText, noClaimsConfirmed]);

  const state: GapInsuranceResolverState = {
    remark,
    file,
    reasonOption,
    otherText,
    noClaimsConfirmed,
    show,
  };

  const handlers: GapInsuranceResolverHandlers = {
    handleReasonOptionChange,
    handleOtherTextChange,
    handleNoClaimsConfirmedChange,
  };

  return {
    state,
    handlers,
    fieldIds,
    alertMessage,
    getCompactAlertStatus,
  };
};
